import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import DescriptionCell from '../components/DescriptionCell';
import './Cart.css';

const formatPrice = (price) =>
  price != null ? `₹${parseFloat(price).toFixed(2)}` : '—';

const formatCutoffTime = (timeStr) =>
  timeStr ? timeStr.slice(0, 5) : null;

const isPastCutoff = (cutoffTimeStr, orderAfterCutoff) => {
  if (!cutoffTimeStr || orderAfterCutoff === 'Y') return false;
  const [hours, minutes] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(hours, minutes, 0, 0);
  return new Date() > cutoff;
};

const Cart = () => {
  const { cart, addToCart, removeFromCart, removeItemCompletely, cartTotal, cartTotalWithoutFee, belowMinimum, hasExemptItem, minOrder, deliveryFee } = useCart();
  const navigate = useNavigate();

  const items = Object.values(cart);

  if (items.length === 0) {
    return (
      <div className="cart-empty">
        <div className="cart-empty-icon">&#128722;</div>
        <h2>Your cart is empty</h2>
        <p>Add products from the home page to get started.</p>
        <button className="cart-shop-btn" onClick={() => navigate('/home')}>
          Browse Products
        </button>
      </div>
    );
  }

  return (
    <div className="cart-container">

      <div className="cart-header">
        <h2 className="cart-title">My Cart</h2>
        <span className="cart-count">{items.length} {items.length === 1 ? 'item' : 'items'}</span>
      </div>

      <div className="cart-items">
        {items.map(({ product, quantity }) => {
          const blocked = isPastCutoff(product.order_cutoff_time, product.order_after_cutoff);
          const lineTotal = product.price != null
            ? parseFloat(product.price) * quantity
            : null;

          const isDeliveryFee = product.id === 1047;

          return (
            <div key={product.id} className="cart-row">

              {/* Image */}
              <div className="cart-row-image">
                {product.image_url
                  ? <img src={product.image_url} alt={product.name} />
                  : <div className="cart-row-image-placeholder">
                      {product.isPack ? '🎁' : '📦'}
                    </div>
                }
              </div>

              {/* Name + description / pack summary + cutoff */}
              <div className="cart-row-info">
                <p className="cart-row-name">{product.name}</p>
                {product.isPack && product.selectionSummary ? (
                  <div className="cart-pack-summary">
                    {product.selectionSummary.map((g, i) => (
                      <p key={i} className="cart-pack-group">
                        <span className="cart-pack-group-name">{g.groupName}:</span>
                        {' '}{g.items.map(it => `${it.name} ×${it.qty}`).join(', ')}
                      </p>
                    ))}
                  </div>
                ) : (
                  product.description && (
                    <DescriptionCell text={product.description} className="cart-row-desc" />
                  )
                )}
                {formatCutoffTime(product.order_cutoff_time) && (
                  <p className={`cart-row-cutoff${blocked ? ' cart-row-cutoff--expired' : ''}`}>
                    Order By {formatCutoffTime(product.order_cutoff_time)}
                    {blocked && ' · Ordering closed'}
                  </p>
                )}
              </div>

              {/* Unit price */}
              <div className="cart-row-price">
                <span className="cart-row-unit-price">{formatPrice(product.price)}</span>
                <span className="cart-row-unit-label">each</span>
              </div>

              {/* Qty control */}
              <div className="cart-row-qty">
                {isDeliveryFee ? (
                  <span className="qty-value">{quantity}</span>
                ) : (
                  <>
                    <button
                      className="qty-btn qty-btn--minus"
                      onClick={() => removeFromCart(product.id)}
                      disabled={quantity === 0 || blocked}
                      aria-label="Remove one"
                    >
                      &#8722;
                    </button>
                    <span className="qty-value">{quantity}</span>
                    <button
                      className="qty-btn qty-btn--plus"
                      onClick={() => addToCart(product)}
                      disabled={blocked}
                      aria-label="Add one"
                    >
                      &#43;
                    </button>
                  </>
                )}
              </div>

              {/* Line total */}
              <div className="cart-row-total">
                {lineTotal != null ? formatPrice(lineTotal) : '—'}
              </div>

              {/* Remove entirely — hidden for delivery fee */}
              {!isDeliveryFee && (
                <button
                  className="cart-row-remove"
                  onClick={() => removeItemCompletely(product.id)}
                  aria-label={`Remove ${product.name}`}
                  title="Remove item"
                >
                  &#10005;
                </button>
              )}
              {isDeliveryFee && <div className="cart-row-remove" />}

            </div>
          );
        })}
      </div>

      {/* Order summary */}
      <div className="cart-summary">
        <div className="cart-summary-row">
          <span>Subtotal ({items.reduce((s, i) => s + i.quantity, 0)} units)</span>
          <span className="cart-summary-total">{formatPrice(cartTotal)}</span>
        </div>
        {belowMinimum && !hasExemptItem && (
          <p className="cart-min-order-msg">
            Add ₹{(minOrder - cartTotalWithoutFee).toFixed(0)} more for free delivery. A delivery fee of ₹{deliveryFee.toFixed(0)} has been added.
          </p>
        )}
        <button
          className="cart-checkout-btn"
          onClick={() => navigate('/checkout')}
        >
          {belowMinimum && !hasExemptItem && (
            <span className="cart-checkout-subfee">Add ₹{(minOrder - cartTotalWithoutFee).toFixed(0)} for free delivery</span>
          )}
          <span>Proceed to Checkout</span>
        </button>
      </div>

    </div>
  );
};

export default Cart;
